/**
 * Real-time seat updates via WebSocket
 *
 * Connects to the public Socket.IO namespace to receive live seat status changes.
 * Provides graceful degradation if WebSocket connection fails.
 */
import { useEffect, useRef, useCallback } from "react";
import { io, Socket } from "socket.io-client";

// Default Railway production URL
const DEFAULT_SOCKET_URL = "https://rutalink-production.up.railway.app";

// Debug mode - only log in development or when explicitly enabled
const DEBUG = !import.meta.env.PROD || localStorage.getItem("debug:websocket") === "true";

function debug(message: string, ...args: unknown[]) {
  if (DEBUG) console.log(message, ...args);
}

/**
 * Get the WebSocket server URL based on environment
 */
function getSocketUrl(): string {
  if (import.meta.env.PROD) {
    return import.meta.env.VITE_API_URL || DEFAULT_SOCKET_URL;
  }
  // In development, connect directly to the backend server
  return "http://localhost:5000";
}

// Event types matching server definitions
export interface SeatHoldEvent {
  tripId: number;
  sessionId: string;
  seatNumbers: string[];
  expiresAt: string;
  userId?: number;
}

export interface SeatReleaseEvent {
  tripId: number;
  sessionId: string;
  seatNumbers: string[];
}

export interface SeatUpdateEvent {
  tripId: number;
  occupiedSeats: string[];
  reservedSeats: string[];
  timestamp: string;
}

interface UseRealtimeSeatsOptions {
  /** Tenant slug for room subscription */
  tenantSlug: string | null;
  /** Trip ID to subscribe to */
  tripId: number | null;
  /** Current session ID to filter out own events */
  sessionId: string | null;
  /** Called when another user holds seats */
  onSeatHold?: (seatNumbers: string[]) => void;
  /** Called when seats are released */
  onSeatRelease?: (seatNumbers: string[]) => void;
  /** Called when seat holds expire */
  onSeatExpire?: (seatNumbers: string[]) => void;
  /** Called when full seat update received */
  onSeatUpdate?: (occupiedSeats: string[], reservedSeats: string[]) => void;
  /** Called when WebSocket connection status changes */
  onConnectionChange?: (connected: boolean) => void;
}

/**
 * Hook for real-time seat updates via WebSocket
 *
 * Connects to the public Socket.IO namespace and subscribes to trip-specific
 * seat events. Provides graceful degradation if WebSocket fails.
 *
 * @example
 * useRealtimeSeats({
 *   tenantSlug: 'pegasos',
 *   tripId: 123,
 *   sessionId: 'abc-123',
 *   onSeatHold: (seats) => markSeatsAsHeld(seats),
 *   onSeatRelease: (seats) => markSeatsAsAvailable(seats),
 * });
 */
export function useRealtimeSeats({
  tenantSlug,
  tripId,
  sessionId,
  onSeatHold,
  onSeatRelease,
  onSeatExpire,
  onSeatUpdate,
  onConnectionChange,
}: UseRealtimeSeatsOptions): void {
  const socketRef = useRef<Socket | null>(null);
  const subscribedRef = useRef(false);

  // Memoize handlers to prevent effect re-runs
  const handleSeatHold = useCallback(
    (data: SeatHoldEvent) => {
      // Ignore our own holds
      if (data.sessionId === sessionId) {
        debug(
          `🔒 [WS] Ignoring own seat hold: [${data.seatNumbers.join(", ")}]`
        );
        return;
      }

      debug(
        `🔒 [WS] Seat hold from another user: [${data.seatNumbers.join(", ")}]`
      );
      onSeatHold?.(data.seatNumbers);
    },
    [sessionId, onSeatHold]
  );

  const handleSeatRelease = useCallback(
    (data: SeatReleaseEvent) => {
      // Ignore our own releases
      if (data.sessionId === sessionId) {
        debug(
          `🔓 [WS] Ignoring own seat release: [${data.seatNumbers.join(", ")}]`
        );
        return;
      }

      debug(
        `🔓 [WS] Seat release from another user: [${data.seatNumbers.join(", ")}]`
      );
      onSeatRelease?.(data.seatNumbers);
    },
    [sessionId, onSeatRelease]
  );

  const handleSeatExpire = useCallback(
    (data: SeatReleaseEvent) => {
      // Don't filter by session - expiration affects everyone
      debug(`⏰ [WS] Seat expired: [${data.seatNumbers.join(", ")}]`);
      onSeatExpire?.(data.seatNumbers);
    },
    [onSeatExpire]
  );

  const handleSeatUpdateEvent = useCallback(
    (data: SeatUpdateEvent) => {
      debug(
        `📊 [WS] Seat update: occupied=${data.occupiedSeats.length}, reserved=${data.reservedSeats.length}`
      );
      onSeatUpdate?.(data.occupiedSeats, data.reservedSeats);
    },
    [onSeatUpdate]
  );

  useEffect(() => {
    // Skip if missing required params
    if (!tenantSlug || !tripId) {
      return;
    }

    const socketUrl = getSocketUrl();

    debug(`🔌 [WS] Connecting to ${socketUrl}/public...`);

    // Create socket connection to public namespace
    const socket = io(`${socketUrl}/public`, {
      transports: ["websocket", "polling"],
      timeout: 10000,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socketRef.current = socket;

    // Connection established
    socket.on("connect", () => {
      debug(`✅ [WS] Connected to public namespace`);
      onConnectionChange?.(true);

      // Subscribe to trip updates
      if (!subscribedRef.current) {
        debug(
          `📡 [WS] Subscribing to trip ${tripId} for tenant ${tenantSlug}`
        );
        socket.emit("public:trip:subscribe", { tenantSlug, tripId });
        subscribedRef.current = true;
      }
    });

    // Connection error
    socket.on("connect_error", (error) => {
      debug(`⚠️ [WS] Connection error (app continues without real-time):`, error.message);
      onConnectionChange?.(false);
    });

    // Disconnection
    socket.on("disconnect", (reason) => {
      debug(`🔌 [WS] Disconnected: ${reason}`);
      onConnectionChange?.(false);
      subscribedRef.current = false;
    });

    // Subscription confirmed
    socket.on(
      "public:trip:subscribed",
      (data: { tenantSlug: string; tripId: number; room: string }) => {
        debug(`✅ [WS] Subscribed to room: ${data.room}`);
      }
    );

    // Server error
    socket.on("error", (error: { message: string }) => {
      debug(`⚠️ [WS] Server error:`, error.message);
    });

    // Seat events
    socket.on("seat:hold", handleSeatHold);
    socket.on("seat:release", handleSeatRelease);
    socket.on("seat:expire", handleSeatExpire);
    socket.on("seat:update", handleSeatUpdateEvent);

    // Cleanup on unmount
    return () => {
      if (subscribedRef.current && socket.connected) {
        debug(`📡 [WS] Unsubscribing from trip ${tripId}`);
        socket.emit("public:trip:unsubscribe", { tenantSlug, tripId });
      }

      socket.off("seat:hold", handleSeatHold);
      socket.off("seat:release", handleSeatRelease);
      socket.off("seat:expire", handleSeatExpire);
      socket.off("seat:update", handleSeatUpdateEvent);

      socket.disconnect();
      socketRef.current = null;
      subscribedRef.current = false;

      debug(`🔌 [WS] Disconnected and cleaned up`);
    };
  }, [
    tenantSlug,
    tripId,
    handleSeatHold,
    handleSeatRelease,
    handleSeatExpire,
    handleSeatUpdateEvent,
    onConnectionChange,
  ]);
}

export default useRealtimeSeats;
